import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Minus, Plus, ShoppingBag, X, ArrowLeft } from "lucide-react";
import { API, RESTAURANT } from "../data/site";

const Order = () => {
  const [skus, setSkus] = useState([]);
  const [taxRate, setTaxRate] = useState(0.06625);
  const [cat, setCat] = useState("");
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("sde_cart") || "[]"));
  const [cartOpen, setCartOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/order-menu`).then((res) => {
      setSkus(res.data.skus);
      setTaxRate(res.data.tax_rate);
      setCat((c) => c || res.data.skus[0]?.category || "");
    }).catch(() => toast.error("Could not load the ordering menu."));
  }, []);

  useEffect(() => { localStorage.setItem("sde_cart", JSON.stringify(cart)); }, [cart]);

  const categories = useMemo(() => [...new Set(skus.map((s) => s.category))], [skus]);
  const visible = useMemo(() => skus.filter((s) => s.category === cat), [skus, cat]);
  const subtotal = cart.reduce((t, i) => t + i.price * i.qty, 0);
  const tax = subtotal * taxRate;
  const count = cart.reduce((t, i) => t + i.qty, 0);

  const add = (sku) => setCart((c) => {
    const f = c.find((i) => i.lookup_key === sku.lookup_key);
    return f ? c.map((i) => (i.lookup_key === sku.lookup_key ? { ...i, qty: i.qty + 1 } : i)) : [...c, { ...sku, qty: 1 }];
  });
  const dec = (key) => setCart((c) =>
    c.map((i) => (i.lookup_key === key ? { ...i, qty: i.qty - 1 } : i)).filter((i) => i.qty > 0)
  );

  const checkout = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Please add your name and phone for pickup.");
      return;
    }
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: cart.map((i) => ({ lookup_key: i.lookup_key, qty: i.qty })),
        name, phone, origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error(`Checkout failed — please call ${RESTAURANT.phone} to order.`);
      setPlacing(false);
    }
  };

  const CartBody = (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto pr-1" data-lenis-prevent>
        {cart.length === 0 && <p className="py-10 text-center font-serif text-xl text-white/40">Your cart is empty — the tawa awaits.</p>}
        {cart.map((i) => (
          <div key={i.lookup_key} className="flex items-center justify-between gap-3 border-b border-white/8 pb-4" data-testid={`cart-item-${i.lookup_key}`}>
            <div className="min-w-0">
              <p className="truncate font-serif text-lg text-white/95">{i.name}</p>
              <p className="text-sm text-gold">${i.price.toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`cart-dec-${i.lookup_key}`} onClick={() => dec(i.lookup_key)} className="glass rounded-full p-1.5 text-white/70 hover:text-gold"><Minus size={13} /></button>
              <span className="w-6 text-center text-sm">{i.qty}</span>
              <button data-testid={`cart-inc-${i.lookup_key}`} onClick={() => add(i)} className="glass rounded-full p-1.5 text-white/70 hover:text-gold"><Plus size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-3 border-t border-white/10 pt-5">
        <div className="flex justify-between text-sm text-mist"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between text-sm text-mist"><span>NJ Sales Tax (6.625%)</span><span>${tax.toFixed(2)}</span></div>
        <div className="flex justify-between font-serif text-2xl text-white"><span>Total</span><span className="text-gold">${(subtotal + tax).toFixed(2)}</span></div>
        <input data-testid="checkout-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pickup name"
          className="w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-gold focus:outline-none" />
        <input data-testid="checkout-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number"
          className="w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-gold focus:outline-none" />
        <button onClick={checkout} disabled={placing || cart.length === 0} data-testid="checkout-pay-button"
          className="btn-gold w-full rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.2em] disabled:opacity-50">
          {placing ? "Redirecting to payment…" : `Pay $${(subtotal + tax).toFixed(2)} & Place Order`}
        </button>
        <p className="text-center text-[11px] uppercase tracking-[0.18em] text-white/35">Secure card payment · Pickup at the counter</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="order-page">
      <header className="glass sticky top-0 z-40">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <a href="/" data-testid="order-back-link" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-gold">
            <ArrowLeft size={14} /> Home
          </a>
          <img src="/logo.png" alt="Sri Dosa Express" className="h-9 w-auto" />
          <button onClick={() => setCartOpen(true)} data-testid="cart-open-button" className="btn-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] lg:hidden">
            <ShoppingBag size={13} /> {count}
          </button>
          <span className="hidden w-16 lg:block" />
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-12 md:px-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Order Online · Pickup</p>
          <h1 className="mt-4 font-serif text-4xl md:text-6xl">Build your <em className="italic text-gold">feast</em></h1>

          <div className="mt-8 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="order-category-tabs">
            {categories.map((c) => (
              <button key={c} data-testid={`order-tab-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${cat === c ? "border-gold bg-gold/10 text-gold" : "border-white/12 text-white/55 hover:border-gold/50 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {visible.map((s) => (
              <div key={s.lookup_key} data-testid={`sku-${s.lookup_key}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-white/10 p-4 transition-colors duration-300 hover:border-gold/50">
                <div className="min-w-0">
                  <p className="truncate font-serif text-lg text-white/95">{s.name}</p>
                  <p className="text-sm text-gold">${s.price.toFixed(2)}</p>
                </div>
                <button onClick={() => add(s)} data-testid={`add-${s.lookup_key}`} aria-label={`Add ${s.name} to cart`}
                  className="glass shrink-0 rounded-full p-2.5 text-gold transition-all duration-300 hover:bg-gold hover:text-ink">
                  <Plus size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <aside className="hidden lg:block">
          <div className="glass sticky top-24 h-[calc(100vh-8rem)] rounded-2xl p-6" data-testid="cart-panel">
            <h2 className="mb-5 flex items-center gap-2 font-serif text-2xl"><ShoppingBag size={18} className="text-gold" /> Your Cart</h2>
            {CartBody}
          </div>
        </aside>
      </main>

      <AnimatePresence>
        {cartOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm lg:hidden" onClick={() => setCartOpen(false)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl border-t border-gold/30 bg-surface p-6"
              data-testid="cart-sheet" onClick={(e) => e.stopPropagation()}>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-serif text-2xl">Your Cart</h2>
                <button onClick={() => setCartOpen(false)} data-testid="cart-close-button" className="glass rounded-full p-2"><X size={16} /></button>
              </div>
              {CartBody}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Order;
